import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  Input,
  ModalFooter,
  Button,
} from '@vitvio/design-system';
import { useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { getLoggerSafe } from '@vitvio/logging';
import type { CreateOperatingRoomModalMutation } from './__generated__/CreateOperatingRoomModalMutation.graphql';

const mutation = graphql`
  mutation CreateOperatingRoomModalMutation($input: CreateOperatingRoomInput!) {
    createOperatingRoom(input: $input) {
      node {
        id
        name
      }
    }
  }
`;

interface Props {
  isOpen: boolean;
  onClose: (shouldReload: boolean) => void;
  hospitalId: string;
}

export function CreateOperatingRoomModal({ isOpen, onClose, hospitalId }: Props) {
  const logger = getLoggerSafe();
  const [operatingRoomName, setOperatingRoomName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [commit] = useMutation<CreateOperatingRoomModalMutation>(mutation);

  const handleHospitalInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setOperatingRoomName(event.target.value || '');

  async function handleSubmit() {
    if (isSubmitting) {
      return;
    }

    if (!operatingRoomName) {
      return;
    }

    setIsSubmitting(true);

    await new Promise((resolve, reject) =>
      commit({
        variables: {
          input: {
            hospitalId,
            name: operatingRoomName,
          },
        },
        onCompleted: (response, errors) => {
          if (errors) {
            logger?.error('GraphQL errors during operating room creation', { errors, hospitalId });
            setIsSubmitting(false);
            reject(errors);
          } else {
            resolve(response);
            setOperatingRoomName('');
            setIsSubmitting(false);
            onClose(true);
          }
        },
        onError: (error) => {
          logger?.error(error as Error, { context: 'createOperatingRoom', hospitalId });
          setIsSubmitting(false);
          reject(error);
        },
      }),
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Operating Room</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input placeholder="Operating room name" value={operatingRoomName} onChange={handleHospitalInputChange} />
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleSubmit}>Create Operating room</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
